import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import {
  AdminContent,
  AdminHeader,
  AdminSidebar,
  useAuthUser,
} from '@employ-engage/common-components';
import parseInt from 'lodash.parseint';
import { NavMenuItem } from '@employ-engage/data';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const UserHome = () => {
  const location = useLocation();
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { signout } = useAuthUser();
  const TRANSITION_DURATION_CLASS = 'duration-300';

  const navMenu: (NavMenuItem | 'horizontal-rule' | null)[] = [];

  navMenu.push(
    {
      title: t('Companies'),
      buttonClassName: 'companies-nav-button',
      iconClassName: 'fa-buildings',
      to: '/user/home',
      current: !!matchRoutes([{ path: '/user/home' }], location),
    },
    {
      title: t('Settings'),
      buttonClassName: 'settings-nav-button',
      iconClassName: 'fa-gear',
      to: '/user/home/settings',
      current: !!matchRoutes([{ path: '/user/home/settings' }], location),
    },
    'horizontal-rule',
    {
      title: t('Logout'),
      buttonClassName: 'logout-nav-button',
      iconClassName: 'fa-right-from-bracket',
      onClick: () => {
        signout()
          .then(() => {
            navigate('/', { state: nanoid() });
          })
          .catch((error) => {
            console.error(error);
          });
      },
    }
  );

  const [adminPageElementRef, setAdminPageElementRef] =
    useState<HTMLElement | null>(null);
  const [adminSidebarElementRef, setAdminSidebarElementRef] =
    useState<HTMLElement | null>(null);
  const [adminHeaderElementRef, setAdminHeaderElementRef] =
    useState<HTMLElement | null>(null);

  let title = '';
  const item = navMenu.find((item) =>
    item !== 'horizontal-rule' ? item?.current : false
  );
  if (item) {
    title = (item as NavMenuItem)?.title || '';
  }

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const updateContent = (
    adminPageElementRef: HTMLElement | null,
    adminHeaderElementRef: HTMLElement | null,
    adminSidebarElementRef: HTMLElement | null
  ) => {
    if (adminPageElementRef && adminHeaderElementRef) {
      setContentHeight(
        adminPageElementRef.clientHeight - adminHeaderElementRef.clientHeight
      );
    }

    if (adminSidebarElementRef && adminPageElementRef) {
      setContentWidth(
        adminPageElementRef.clientWidth - adminSidebarElementRef.clientWidth
      );
    }
  };

  const handleResize = () =>
    updateContent(
      adminPageElementRef,
      adminHeaderElementRef,
      adminSidebarElementRef
    );

  useEffect(() => {
    if (
      adminPageElementRef &&
      adminHeaderElementRef &&
      adminSidebarElementRef
    ) {
      window.addEventListener('resize', handleResize);
      handleResize();
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adminPageElementRef, adminSidebarElementRef, adminHeaderElementRef]);
  return (
    <div
      ref={setAdminPageElementRef}
      className="flex flex-row font-['Poppins'] fixed top-0 left-0 w-[100vw]"
    >
      <AdminSidebar
        transitionDurationClass={TRANSITION_DURATION_CLASS}
        ref={setAdminSidebarElementRef}
        navMenu={navMenu}
        isCollapsed={isCollapsed}
        setIsCollapsed={(isCollapsed) => {
          setIsCollapsed(isCollapsed);
          setTimeout(() => {
            updateContent(
              adminPageElementRef,
              adminHeaderElementRef,
              adminSidebarElementRef
            );
          }, parseInt(TRANSITION_DURATION_CLASS.replace('duration-', ''), 10));
        }}
      />
      <div className="flex-grow">
        <AdminHeader ref={setAdminHeaderElementRef} title={title} isMainPage />
        <AdminContent
          transitionDurationClass={TRANSITION_DURATION_CLASS}
          contentHeight={contentHeight}
          contentWidth={contentWidth}
        />
      </div>
    </div>
  );
};

export default UserHome;
